/**
 * Van/Naar van een reno-mail, voor de kolommen VAN en NAAR in het e-mailverloop.
 *
 * De ontvanger van een verzonden mail staat **enkel** in `metadata` (`to`, `cc`, `bcc`,
 * gezet door `RenoHelpdeskService.sendEmail`): `email.from` is dan het reno-postvak
 * (`leo@yally.be`). Zonder deze afleiding stond op elke verzonden rij hetzelfde adres en
 * moest je de mail openklikken om te zien naar wie ze vertrokken was.
 */

/** Minimale vorm die nodig is om afzender en ontvanger te bepalen. */
export interface RenoParticipantEmail {
  direction?: 'sent' | 'received';
  from?: string | null;
  metadata?: {
    /** null wanneer de mail zonder zichtbare geadresseerde vertrok (alles in CC/BCC). */
    to?: string | null;
    cc?: string[] | null;
    bcc?: string[] | null;
  } | null;
}

export interface RenoEmailFromTo {
  /** Afzender: naam als die gekend is, anders het adres. */
  from: string;
  /** Ontvanger: naam als die gekend is, anders het adres. `—` als er niets bewaard is. */
  to: string;
  /** Aantal bijkomende ontvangers (CC/BCC) naast de weergegeven ontvanger. */
  extraCount: number;
  /** Volledige opsplitsing Aan/CC/BCC voor de tooltip. */
  tooltip: string;
}

/** Naam bij een adres opzoeken; standaard blijft het adres zelf staan. */
export type RenoNameResolver = (address: string) => string;

/** Weergave voor een ontbrekend adres — een lege cel leest als een fout. */
export const GEEN_ADRES = '—';

const schoon = (adressen: string[] | null | undefined): string[] =>
  (adressen ?? []).map((a) => a?.trim()).filter((a): a is string => !!a);

export const describeRenoEmailFromTo = (
  email: RenoParticipantEmail | null | undefined,
  resolveName: RenoNameResolver = (address) => address,
): RenoEmailFromTo => {
  const from = email?.from?.trim() ?? '';
  const to = email?.metadata?.to?.trim() ?? '';
  const cc = schoon(email?.metadata?.cc);
  const bcc = schoon(email?.metadata?.bcc);

  // Zonder TO (alles in CC/BCC) zou de kolom leeg blijven terwijl de mail wél ontvangers
  // had. Toon dan wie ze gekregen heeft.
  const ontvangers = to ? [to, ...cc, ...bcc] : [...cc, ...bcc];
  const eerste = ontvangers[0] ?? '';

  const tooltip = ontvangers.length
    ? [
        to ? `Aan: ${to}` : 'Aan: — (enkel CC/BCC)',
        cc.length ? `CC: ${cc.join(', ')}` : null,
        bcc.length ? `BCC: ${bcc.join(', ')}` : null,
      ]
        .filter(Boolean)
        .join(' · ')
    : 'Geen ontvanger bewaard bij deze mail';

  return {
    from: from ? resolveName(from) : GEEN_ADRES,
    to: eerste ? resolveName(eerste) : GEEN_ADRES,
    extraCount: Math.max(0, ontvangers.length - 1),
    tooltip,
  };
};

/**
 * Bouwt een naamopzoeker uit {name, email}-lijsten (ontvangerskiezer, leveranciers,
 * contactpersoon, …). Onbekende adressen blijven als adres staan — nooit een lege cel.
 */
export const buildRenoNameResolver = (
  known: Array<{ name?: string | null; email?: string | null } | null | undefined>,
): RenoNameResolver => {
  const perAdres = new Map<string, string>();
  for (const entry of known) {
    const email = entry?.email?.trim().toLowerCase();
    const name = entry?.name?.trim();
    if (email && name && !perAdres.has(email)) perAdres.set(email, name);
  }
  return (address: string) => perAdres.get(address.trim().toLowerCase()) ?? address;
};
